import React, { useEffect, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import { 
  FiShoppingCart, 
  FiPackage, 
  FiDollarSign, 
  FiAlertTriangle, 
  FiStar, 
  FiTrello, 
  FiArrowUpRight
} from "react-icons/fi";
import axios from "axios";
import { baseurl } from "../baseurl";

function Home() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const vendorId = localStorage.getItem("vendorId");

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}getVendorDashboardStats/${vendorId}`);
      if (res.data.success) setData(res.data);
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vendorId) fetchDashboardData();
  }, [vendorId]);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner-grow text-primary" role="status"></div>
      </div>
    );
  }

  const { analytics, recentOrders } = data;

  const chartData = [
    { name: "Live", count: analytics.liveProducts, color: "#10b981" },
    { name: "Total", count: analytics.totalProducts, color: "#6366f1" },
    { name: "Low Stock", count: analytics.lowStockAlerts, color: "#f43f5e" },
  ];

  return (
    <div className="dashboard-app">
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="content-container">
          
          {/* Header Section */}
          <div className="dashboard-header mb-5">
            <div>
              <h4 className="fw-bold text-dark mb-1">Welcome back, Vendor!</h4>
              <p className="text-muted small">Here's what's happening with your store today.</p>
            </div>
            {/* <button className="btn btn-primary-glass d-flex align-items-center gap-2">
               <FiArrowUpRight /> View Shop
            </button> */}
          </div>

          {/* KPI Row */}
          <div className="row g-4 mb-5">
            {[
              { label: "Revenue", val: `₹${analytics.revenue.toLocaleString()}`, icon: <FiDollarSign />, color: "indigo" },
              { label: "Orders", val: analytics.ordersCount, icon: <FiShoppingCart />, color: "emerald" },
              { label: "Rating", val: `${analytics.rating} / 5`, icon: <FiStar />, color: "amber" },
              { label: "Tickets", val: analytics.activeTickets, icon: <FiTrello />, color: "rose" }
            ].map((kpi, i) => (
              <div className="col-12 col-sm-6 col-xl-3" key={i}>
                <div className={`modern-stat-card ${kpi.color}`}>
                  <div className="icon-box">{kpi.icon}</div>
                  <div className="stat-info">
                    <span className="label">{kpi.label}</span>
                    <h3 className="value">{kpi.val}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-4 mb-5">
            {/* Chart Area */}
            <div className="col-lg-8">
              <div className="glass-card shadow-sm h-100">
                <div className="card-header-modern">
                  <h6 className="fw-bold m-0">Inventory Performance</h6>
                  <select className="form-select form-select-sm w-auto">
                    <option>Real-time</option>
                  </select>
                </div>
                <div className="card-body-modern">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} />
                      <Tooltip 
                        cursor={{fill: '#f8fafc'}} 
                        contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}}
                      />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={45}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Health Checklist */}
            <div className="col-lg-4">
              <div className="glass-card shadow-sm h-100">
                <div className="card-header-modern">
                  <h6 className="fw-bold m-0">Store Health</h6>
                </div>
                <div className="card-body-modern p-4">
                  <div className="health-item mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="small text-muted">Stock Level</span>
                      <span className="small fw-bold">78%</span>
                    </div>
                    <div className="progress-modern"><div className="progress-inner" style={{width: '78%'}}></div></div>
                  </div>
                  
                  <div className="health-metrics">
                     <div className="metric-row border-bottom">
                        <span className="text-muted small">Total Catalog</span>
                        <span className="fw-bold text-dark">{analytics.totalProducts}</span>
                     </div>
                     <div className="metric-row border-bottom">
                        <span className="text-muted small">Approved</span>
                        <span className="fw-bold text-success">{analytics.liveProducts}</span>
                     </div>
                     <div className="metric-row">
                        <span className="text-muted small">Low Stock</span>
                        <span className="fw-bold text-danger">{analytics.lowStockAlerts}</span>
                     </div>
                  </div>

                  <div className="review-pill mt-4">
                    <div className="d-flex align-items-center gap-3">
                      <div className="review-avatar">{analytics.totalReviews}</div>
                      <div>
                        <p className="m-0 fw-bold small">Customer Reviews</p>
                        <p className="m-0 text-muted x-small">Latest feedback received</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="glass-card shadow-sm mb-5">
            <div className="card-header-modern">
              <h6 className="fw-bold m-0">Recent Transactions</h6>
              {/* <button className="btn btn-link btn-sm text-decoration-none p-0">View All</button> */}
            </div>
            <div className="p-0">
              <div className="table-responsive">
                <table className="table table-custom">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((txn) => (
                      <tr key={txn.order_id}>
                        <td className="fw-medium text-dark">#{txn.order_uuid.substring(0, 8)}</td>
                        <td className="text-muted small">{new Date(txn.created_at).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'})}</td>
                        <td className="fw-bold text-dark">₹{txn.amount}</td>
                        <td>
                          <span className={`status-pill ${txn.order_status.toLowerCase()}`}>
                            {txn.order_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .dashboard-app {
          font-family: 'Inter', sans-serif;
          background-color: #f1f5f9;
        }

        .main-wrapper {
          padding-top: 20px;
          min-height: 100vh;
        }

        .content-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        /* Modern Stat Cards */
        .modern-stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 1.25rem;
          transition: all 0.3s ease;
          border: 1px solid rgba(255,255,255,0.8);
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
        }

        .modern-stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
        }

        .icon-box {
          width: 54px;
          height: 54px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }

        /* Color Variations */
        .indigo .icon-box { background: #eef2ff; color: #6366f1; }
        .emerald .icon-box { background: #ecfdf5; color: #10b981; }
        .amber .icon-box { background: #fffbeb; color: #f59e0b; }
        .rose .icon-box { background: #fff1f2; color: #f43f5e; }

        .stat-info .label { font-size: 13px; color: #64748b; font-weight: 500; }
        .stat-info .value { font-size: 20px; font-weight: 700; color: #1e293b; margin: 0; }

        /* Glass Cards */
        .glass-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 24px;
          border: 1px solid rgba(255,255,255,1);
        }

        .card-header-modern {
          padding: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #f1f5f9;
        }

        .card-body-modern { padding: 1.5rem; }

        /* Health Section */
        .progress-modern { height: 8px; background: #f1f5f9; border-radius: 10px; overflow: hidden; }
        .progress-inner { height: 100%; background: linear-gradient(90deg, #6366f1, #818cf8); border-radius: 10px; }
        
        .metric-row { display: flex; justify-content: space-between; padding: 12px 0; }
        
        .review-pill {
          background: #f8fafc;
          padding: 1rem;
          border-radius: 16px;
        }
        .review-avatar {
          width: 40px; height: 40px; background: #1e293b; color: white;
          border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: bold;
        }

        /* Custom Table */
        .table-custom { margin: 0; }
        .table-custom thead th {
          background: #f8fafc;
          padding: 1rem 1.5rem;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          border-top: none;
        }
        .table-custom tbody td {
          padding: 1.25rem 1.5rem;
          vertical-align: middle;
          border-color: #f1f5f9;
        }

        /* Status Pills */
        .status-pill {
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          text-transform: capitalize;
        }
        .status-pill.pending { background: #fff7ed; color: #ea580c; }
        .status-pill.delivered { background: #f0fdf4; color: #16a34a; }
        .status-pill.shipped { background: #eff6ff; color: #2563eb; }

        .btn-primary-glass {
          background: #6366f1;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.3);
        }

        .loader-container {
          height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
        }

        .x-small { font-size: 10px; }
      `}</style>
    </div>
  );
}

export default Home;